import {
  ApiErrorResponse,
  DataRowType,
  EmptyData,
  ForbiddenData,
  ISODate,
  InputDataType,
  isNotFound,
} from '@atlas-frontend/sdk';
import {
  ForwardedRef,
  forwardRef,
  ReactElement,
  useEffect,
  useState,
} from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { AppErrorBoundaryFallback } from '../../../errors/AppErrorBoundary';
import styled from 'styled-components';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './InputDataSampleTable.translations.json';
import { InputDataViewTable, TableFilterSortProps } from './InputDataViewTable';
import { Table } from '@tanstack/react-table';
import { Flex } from 'antd';

const { addTranslations, t } = i18nNsFactory('table-no-data-cell');
addTranslations(translations);

interface InputDataSampleFallbackProps extends FallbackProps {
  date?: ISODate;
  id?: number;
}

function InputDataSampleFallback(props: InputDataSampleFallbackProps) {
  const { date, id, error, resetErrorBoundary } = props;
  const [state, setState] = useState({ date, id });

  useEffect(() => {
    if (date !== state.date || id !== state.id) {
      // Reset error if date or id changed in URL
      resetErrorBoundary();
      setState({ date, id });
    }
  }, [date, id, resetErrorBoundary, state]);

  return (
    <ErrorWrapper>
      {isNotFound(error) ? (
        <EmptyData>{t('input.notReady')}</EmptyData>
      ) : (
        <AppErrorBoundaryFallback {...props} />
      )}
    </ErrorWrapper>
  );
}

export function InputDataError({ error }: { error: ApiErrorResponse }) {
  const apiError = error.response?.data?.errors?.[0];
  const errorCode = apiError?.code;
  const ErrorElement =
    errorCode === 'missing_view_permission' ? ForbiddenData : EmptyData;

  let content: ReactElement | string;
  if (errorCode === 'missing_view_permission') {
    content = (
      <Flex vertical gap="small">
        <strong>{t('missing_view_permission.title')}</strong>
        {t('missing_view_permission.description')}
      </Flex>
    );
  } else if (apiError) {
    content = t(`alert.error.code.${errorCode}`, { ...apiError?.meta?.args });
  } else {
    content = t('input.notReady');
  }

  return (
    <ErrorWrapper>
      <ErrorElement>{content}</ErrorElement>
    </ErrorWrapper>
  );
}

interface InputDataSampleTableProps {
  id: number;
  date: ISODate;
  groupId?: number;
  outputDataVersionId?: number;
  data?: InputDataType;
  isFetching?: boolean;
  error?: ApiErrorResponse | null;
  tableProps?: TableFilterSortProps;
  subjectNameMappings: Record<string, string>;
}

export const InputDataSampleTable = forwardRef(
  (
    {
      date,
      id,
      groupId,
      outputDataVersionId,
      data,
      isFetching,
      error,
      tableProps,
      subjectNameMappings,
    }: InputDataSampleTableProps,
    ref: ForwardedRef<Table<DataRowType>>,
  ) => {
    if (error) {
      return <InputDataError error={error} />;
    }

    return (
      <ErrorBoundary
        FallbackComponent={(fallbackProps) => (
          <InputDataSampleFallback {...fallbackProps} date={date} id={id} />
        )}
      >
        <InputDataViewTable
          data={data}
          date={date}
          descriptionId={id}
          groupId={groupId}
          isLoading={isFetching}
          ref={ref}
          outputDataVersionId={outputDataVersionId}
          stateStorageKey="inputDataViewTable"
          tableProps={tableProps}
          subjectNameMappings={subjectNameMappings}
        />
      </ErrorBoundary>
    );
  },
);

const ErrorWrapper = styled.div`
  position: relative;
  top: -50px;
  display: flex;
  align-self: center;
`;
